import { Node } from "@tiptap/core";

export interface DirectivePhoto { src: string; alt: string }

export interface ParsedDirective {
  kind: "photo" | "embed" | "bookmark" | "button";
  label: string;
  attrs: Record<string, string>;
  caption: string;
}

/**
 * Attribute values sit in double quotes inside `{…}`, so the characters that would end them (or
 * the block) are written as character references. remark-directive decodes them when serving.
 */
export function encodeAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/\\/g, "&#92;").replace(/\}/g, "&#125;").replace(/\s+/g, " ").trim();
}
export function decodeAttr(value: string): string {
  return value.replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code))).replace(/&quot;/g, '"').replace(/&amp;/g, "&");
}

/**
 * A photo the post owns: `::photo{asset="<assetId>" version=<n> alt="…" width=wide}`, then an
 * optional `: caption` line. The alt text belongs to this use, not to any library record.
 */
export function photoDirective(photo: { assetId: string; version: number; alt: string }, options: { wide?: boolean; caption?: string } = {}): string {
  const caption = (options.caption ?? "").replace(/\s+/g, " ").trim();
  return `::photo{asset="${photo.assetId}" version=${photo.version} alt="${encodeAttr(photo.alt)}"${options.wide ? " width=wide" : ""}}${caption ? `\n: ${caption}` : ""}`;
}

/** Every photo the body uses, with the alt text each use gives it, in order of first use. */
export function photoUses(markdown: string): Array<{ assetId: string; version: number; alt: string }> {
  const found = new Map<string, { assetId: string; version: number; alt: string }>();
  for (const line of markdown.split("\n")) {
    if (!line.startsWith("::photo")) continue;
    const parsed = parseDirective(line);
    const { asset, version } = parsed?.attrs ?? {};
    if (!asset || !version || !/^[1-9][0-9]*$/.test(version)) continue;
    found.set(`${asset}/${version}`, { assetId: asset, version: Number(version), alt: parsed!.attrs.alt ?? "" });
  }
  return [...found.values()];
}

export type LinkBlockKind = "embed" | "bookmark" | "button";

/**
 * A link as typed on a phone: no spaces, and the scheme and site in lowercase (a capitalised "Https://"
 * isn't a link). The rest keeps its case, because paths and video IDs are case-sensitive.
 */
export function typedUrl(value: string): string {
  return value.replace(/\s+/g, "").replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i, (start) => start.toLowerCase());
}

/** A video or post, link card or button, from what the owner typed. Empty text gives the reader-facing default. */
export function linkDirective(kind: LinkBlockKind, url: string, label = ""): string {
  const href = `"${url.trim().replace(/"/g, "")}"`;
  const text = label.replace(/[[\]\n]/g, "").trim();
  if (kind === "embed") return `::embed{url=${href}}`;
  if (kind === "bookmark") return `::bookmark${text ? `[${text}]` : ""}{url=${href}}`;
  return `::button[${text || "Find out more"}]{url=${href}}`;
}

/** The same photo with a new caption line; an empty caption removes it. */
export function withCaption(raw: string, caption: string): string {
  const head = raw.split("\n")[0]!;
  const text = caption.replace(/\s+/g, " ").trim();
  return text ? `${head}\n: ${text}` : head;
}

/** The same photo, shown across the page or at the text's width. */
export function withWide(raw: string, wide: boolean): string {
  const [head, ...rest] = raw.split("\n");
  const narrow = head!.replace(/ width=wide(?=[ }])/, "");
  const next = wide ? narrow.replace(/\}$/, " width=wide}") : narrow;
  return [next, ...rest].join("\n");
}

/** Who shows a video or post, for its placeholder. */
export function embedSource(url: string): { name: string; video: boolean } {
  const site = host(url);
  if (/(^|\.)(youtube\.com|youtu\.be)$/.test(site)) return { name: "YouTube", video: true };
  if (/(^|\.)vimeo\.com$/.test(site)) return { name: "Vimeo", video: true };
  if (/(^|\.)instagram\.com$/.test(site)) return { name: "Instagram", video: false };
  if (/(^|\.)(x\.com|twitter\.com)$/.test(site)) return { name: "X", video: false };
  return { name: site, video: false };
}

/** Reads a directive's parts for display only; the raw text is what's saved. */
export function parseDirective(raw: string): ParsedDirective | null {
  const head = /^::(photo|embed|bookmark|button)(?:\[([^\]\n]*)\])?(?:\{([^}\n]*)\})?/.exec(raw);
  if (!head) return null;
  const attrs: Record<string, string> = {};
  for (const match of (head[3] ?? "").matchAll(/([\w-]+)=(?:"([^"]*)"|(\S+))/g)) attrs[match[1]!] = decodeAttr(match[2] ?? match[3] ?? "");
  return { kind: head[1] as ParsedDirective["kind"], label: head[2] ?? "", attrs, caption: /\n: ([^\n]*)$/.exec(raw)?.[1] ?? "" };
}

export const KIND_LABEL: Record<ParsedDirective["kind"], string> = { photo: "Photo", embed: "Video or post", bookmark: "Link card", button: "Button" };

/** Lucide strokes, inlined because a node view is plain DOM. */
const ICON = {
  play: '<polygon points="6 3 20 12 6 21 6 3"/>',
  external: '<path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>',
  post: '<rect width="18" height="18" x="3" y="3" rx="2"/><path d="M7 8h10"/><path d="M7 12h10"/><path d="M7 16h6"/>',
};
function icon(name: keyof typeof ICON, size: number): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  for (const [key, value] of Object.entries({ width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": 2, "stroke-linecap": "round", "stroke-linejoin": "round", "aria-hidden": "true" })) svg.setAttribute(key, String(value));
  svg.innerHTML = ICON[name];
  return svg;
}
function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function host(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

/** A single opaque block keeps the platform's Markdown directives byte for byte through edits, shown as what it adds to the post. */
export const BlogDirective = Node.create<{ photo: (assetId: string, version: number) => DirectivePhoto | undefined }>({
  name: "blogDirective",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  addOptions() { return { photo: () => undefined }; },
  addAttributes() { return { raw: { default: "" } }; },
  parseHTML() { return [{ tag: "div[data-blog-directive]", getAttrs: (element) => ({ raw: element.getAttribute("data-raw") ?? "" }) }]; },
  renderHTML({ node }) { return ["div", { "data-blog-directive": "", "data-raw": node.attrs.raw, class: "bw-blog-directive" }, node.attrs.raw]; },
  addNodeView() {
    return ({ node }) => {
      const raw = String(node.attrs.raw ?? "");
      const parsed = parseDirective(raw);
      const dom = document.createElement("div");
      dom.className = "bw-blog-directive";
      dom.dataset.blogDirective = "";
      dom.dataset.raw = raw;
      dom.contentEditable = "false";
      if (!parsed) {
        dom.textContent = raw;
        return { dom };
      }
      dom.dataset.kind = parsed.kind;
      dom.setAttribute("aria-label", KIND_LABEL[parsed.kind]);
      const url = parsed.attrs.url ?? "";
      if (parsed.kind === "photo") {
        const known = parsed.attrs.asset && /^[1-9][0-9]*$/.test(parsed.attrs.version ?? "") ? this.options.photo(parsed.attrs.asset, Number(parsed.attrs.version)) : undefined;
        const figure = document.createElement("figure");
        if (parsed.attrs.width === "wide") figure.dataset.wide = "";
        if (known) {
          const img = document.createElement("img");
          img.src = known.src;
          img.alt = parsed.attrs.alt ?? known.alt;
          figure.append(img);
        } else figure.append(element("div", "bw-blog-directive-missing", "This photo can't be found, so it won't show."));
        if (parsed.caption) figure.append(element("figcaption", "", parsed.caption));
        dom.append(figure);
      } else if (parsed.kind === "embed") {
        // Readers get the provider's own player or post; the editor shows where it will sit.
        const source = embedSource(url);
        const frame = element("div", "bw-blog-embed");
        frame.dataset.video = String(source.video);
        const badge = element("span", "bw-blog-embed-badge");
        badge.append(icon(source.video ? "play" : "post", source.video ? 22 : 20));
        frame.append(badge, element("strong", "", source.video ? `${source.name} video` : `${source.name} post`), element("small", "", url));
        dom.append(frame);
      } else if (parsed.kind === "bookmark") {
        const card = element("div", "bw-blog-bookmark");
        const text = element("span", "bw-blog-bookmark-text");
        text.append(element("strong", "", parsed.label || host(url)), element("small", "", host(url)));
        const mark = element("span", "bw-blog-bookmark-icon");
        mark.append(icon("external", 18));
        card.append(text, mark);
        dom.append(card);
      } else {
        const row = element("div", "bw-blog-button");
        row.append(element("span", "bw-blog-button-face", parsed.label || "Find out more"), element("small", "", url ? `Goes to ${url}` : "No link yet"));
        dom.append(row);
      }
      return { dom };
    };
  },
  markdownTokenizer: {
    name: "blogDirective",
    level: "block",
    start: (source) => source.indexOf("::"),
    tokenize: (source) => {
      const match = /^::(?:photo|embed|bookmark|button)(?:\[[^\]\n]*\])?(?:\{[^}\n]*\})?(?:\n: [^\n]*)?(?=\n|$)/.exec(source);
      return match ? { type: "blogDirective", raw: match[0], text: match[0] } : undefined;
    },
  },
  parseMarkdown: (token) => ({ type: "blogDirective", attrs: { raw: token.raw } }),
  renderMarkdown: (node) => String(node.attrs?.raw ?? ""),
});
